import { useState } from 'react';
import { Plus, Waves } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FarmCreateModal } from './FarmCreateModal';

interface NoFarmsEmptyStateProps {
  /** Operators get a Create Farm CTA; everyone else is told to contact one. */
  isOperator: boolean;
}

/**
 * Shown on /overview and /map when the user has no farms — replaces the old
 * dead-end "Failed to load farm data" error that appeared for fresh operators
 * and unassigned members.
 */
export function NoFarmsEmptyState({ isOperator }: NoFarmsEmptyStateProps) {
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Card>
      <Card.Body>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center mb-4">
            <Waves className="h-7 w-7 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No farms yet</h3>
          {isOperator ? (
            <>
              <p className="text-sm text-gray-500 mt-1 mb-6 max-w-sm">
                Create your first farm to start monitoring ponds, cycles, and water quality.
              </p>
              <Button
                variant="primary"
                leftIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                Create Farm
              </Button>
            </>
          ) : (
            <p className="text-sm text-gray-500 mt-1 max-w-sm">
              You don't have a farm assigned yet. Please contact your operator to be added to a farm.
            </p>
          )}
        </div>
      </Card.Body>

      <FarmCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Card>
  );
}
